import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { addSubscriber, isSupabaseConfigured } from '@/lib/supabase';
import { springPresets } from '@/lib/motion';
import { toast } from 'sonner';

const SubscribeSection: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isSupabaseConfigured) {
      toast.error('後端尚未設定', { description: '請先在 .env 填入 Supabase 連線資訊' });
      return;
    }
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error('請輸入有效的 Email 地址');
      return;
    }
    setLoading(true);
    try {
      await addSubscriber(trimmedEmail, name.trim() || undefined);
      setSubmitted(true);
      toast.success('訂閱成功！感謝您的支持 🎉', {
        description: '我們會第一時間把最新 AI 工具資訊寄給您',
      });
      setEmail('');
      setName('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('duplicate') || msg.includes('already')) {
        toast.info('您已經訂閱過了！', { description: '我們的電子報會定期寄到您的信箱' });
        setSubmitted(true);
      } else {
        toast.error('訂閱失敗，請稍後再試');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="subscribe" className="py-16 px-4">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={springPresets.gentle}
        className="max-w-2xl mx-auto"
      >
        {/* Card */}
        <div
          className="relative overflow-hidden rounded-3xl p-8 sm:p-10"
          style={{
            background: 'linear-gradient(135deg, #fdf4ff 0%, #fff7ed 50%, #f0f9ff 100%)',
            border: '1.5px solid rgba(168,85,247,0.15)',
            boxShadow: '0 8px 40px rgba(168,85,247,0.08), 0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          {/* Decorative blobs */}
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-20 pointer-events-none"
            style={{ background: 'radial-gradient(circle, #a855f7, transparent 70%)' }} />
          <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full opacity-15 pointer-events-none"
            style={{ background: 'radial-gradient(circle, #f97316, transparent 70%)' }} />

          {/* Content */}
          <div className="relative">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4"
              style={{ background: 'rgba(168,85,247,0.1)', color: '#9333ea', border: '1px solid rgba(168,85,247,0.2)' }}>
              <Mail size={12} />
              免費電子報
            </div>

            <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-2">
              訂閱最新 AI 工具資訊
            </h2>
            <p className="text-gray-500 text-sm mb-8 leading-relaxed">
              Tracy 精選每週最值得關注的 AI 工具與教學，直接寄到您的信箱，不錯過任何 AI 新動態 ✨
            </p>

            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={springPresets.bouncy}
                  className="flex flex-col items-center gap-3 py-6 text-center"
                >
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{ background: 'rgba(34,197,94,0.1)', border: '1.5px solid rgba(34,197,94,0.25)' }}>
                    <CheckCircle size={28} className="text-green-500" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-lg">成功訂閱！</p>
                    <p className="text-sm text-gray-500 mt-1">我們已記錄您的 Email，Tracy 會在有新內容時通知您 🎉</p>
                  </div>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-xs text-purple-500 hover:text-purple-700 transition-colors mt-1"
                  >
                    使用另一個 Email 訂閱
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  className="space-y-3"
                >
                  {/* Name input */}
                  <div>
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="您的稱呼（選填）"
                      disabled={loading}
                      className="w-full px-4 py-3 rounded-2xl text-sm text-gray-800 placeholder-gray-400 outline-none transition-all disabled:opacity-60"
                      style={{
                        background: '#fff',
                        border: '1.5px solid #e2e8f0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      }}
                      onFocus={e => { e.currentTarget.style.borderColor = '#a855f7'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.1)'; }}
                      onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
                    />
                  </div>

                  {/* Email + button row */}
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      disabled={loading}
                      className="flex-1 px-4 py-3 rounded-2xl text-sm text-gray-800 placeholder-gray-400 outline-none transition-all disabled:opacity-60"
                      style={{
                        background: '#fff',
                        border: '1.5px solid #e2e8f0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      }}
                      onFocus={e => { e.currentTarget.style.borderColor = '#a855f7'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.1)'; }}
                      onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
                    />
                    <motion.button
                      type="submit"
                      disabled={loading || !email.trim()}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      className="flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-bold text-white transition-all disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
                      style={{
                        background: 'linear-gradient(135deg, #a855f7, #f97316)',
                        boxShadow: '0 4px 16px rgba(168,85,247,0.3)',
                      }}
                    >
                      {loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                      {loading ? '訂閱中...' : '立即訂閱'}
                    </motion.button>
                  </div>

                  <p className="text-xs text-gray-400 text-center">
                    免費訂閱，隨時可取消 · 不會有垃圾郵件
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default SubscribeSection;
